/**
 * Authentication store (Pinia)
 */
import {defineStore} from 'pinia'
import apiClient, {abortAllRequests, clearAuthStorage} from '@/api/client'
import axios from 'axios'
import {
  awaitBootGate,
  clearAccessToken,
  clearRefreshToken,
  getAccessToken,
  getRefreshToken,
  loadStoredTokens,
  persistRefreshToken,
  purgeLegacyStoredTokens,
  refreshAccessToken,
  scheduleProactiveRefresh,
  setAccessToken,
  setRefreshToken
} from '@/auth/tokenManager'
import { shouldClearNativeRefreshOnLoginPurge } from '@/auth/nativeBootSession'
import {isNative} from '@/native/platform'
import {setLastLoginPhone} from '@/native/lastLoginPhone'
import {secureGet} from '@/native/secureStorage'
import {primeAppVersion, clearAppVersionCache} from '@/composables/useAppVersion'
import {clearLayoutCache} from '@/composables/useInterfaceLayout'
import {resetNativeSessionRights} from '@/auth/nativeSessionRights'

/**
 * Match RBAC route codes in the UI. Keep module wildcards explicit at the
 * call site so a broad `{module}.*` grant cannot silently unlock unrelated
 * screens across the app.
 */
export function permissionMatches(permissions, code, user = null) {
  if (!code) return true
  if (!permissions || !Array.isArray(permissions)) return false
  if (user?.is_superadmin || user?.is_superuser) return true
  if (permissions.includes('superadmin')) return true
  if (permissions.includes(code)) return true
  return false
}

/**
 * Seed the technician app interface decision (v1/v2 + version + revision) from
 * the login response so the correct shell mounts on the FIRST frame — no
 * v1<->v2 jump, no "плывёт". Backend resolved the exact same decision the
 * running app would, so we just persist it before the tech app boots.
 */
function _primeTechInterface(data) {
  try {
    if (!data || !data.is_technician) return
    primeAppVersion(
      {
        app_v2_enabled: data.app_v2_enabled,
        interface_version: data.interface_version,
        interface_revision: data.interface_revision,
      },
      data.user?.id,
    )
  } catch { /* never block login on a cache-priming issue */ }
}

const _API_BASE = (import.meta.env?.VITE_API_BASE_URL || '').replace(/\/+$/, '')
const TECH_LOGIN_URL = _API_BASE
  ? `${_API_BASE}/api/v1/auth/technician-login/`
  : '/api/v1/auth/technician-login/'

// God Mode uses sessionStorage for tab isolation
function _getSessionItem(key) {
  try { return sessionStorage.getItem(key) } catch { return null }
}
function _getLocalItem(key) {
  try { return localStorage.getItem(key) } catch { return null }
}
function _storage() {
  return _getSessionItem('god_mode') ? sessionStorage : localStorage
}
function _getItem(key) {
  return _getSessionItem(key) || _getLocalItem(key)
}
function _readStorageItem(key) {
  try {
    return { available: true, value: _storage().getItem(key) }
  } catch {
    return { available: false, value: null }
  }
}

// A login response that carries no interface_version must still overwrite the
// stored one: the value is read back and sent to the server as the technician's
// interface (see technicianBiometricTokenLogin), so keeping the previous
// company's id would hand this session another company's preset. Hence a
// sentinel rather than `undefined` — only callers that pass nothing keep the id.
const _KEEP_INTERFACE_VERSION = Symbol('keep-interface-version')

function _setTechnicianSession(interfaceVersion = _KEEP_INTERFACE_VERSION) {
  try {
    localStorage.setItem('is_technician_session', 'true')
    if (interfaceVersion !== _KEEP_INTERFACE_VERSION) {
      localStorage.setItem('interface_version_id', interfaceVersion || '')
    }
    return true
  } catch (error) {
    console.warn('[Auth] Could not persist technician session:', error?.message || error)
    return false
  }
}

/** Whether the client currently holds credentials worth treating as a session. */
function _hasValidSession() {
  if (_getSessionItem('god_mode')) {
    return !!_getSessionItem('access_token')
  }
  // Native: only an in-memory access token counts as logged-in. A stale refresh
  // token in Keychain must NOT flip isAuthenticated — that fires dialer/orch/
  // entitlements on the login screen and aborts password login.
  if (isNative()) {
    return !!getAccessToken()
  }
  if (getAccessToken() || getRefreshToken()) return true
  // Web cookie-only sessions may have purged JS tokens but keep a cached profile;
  // boot silent-refresh rehydrates from the httpOnly cookie.
  if (_getItem('user')) return true
  return false
}

/** Parse a JSON value from auth storage; corrupt data must never break store init. */
function _getJSON(key) {
  try {
    const raw = _getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    console.warn(`[Auth] Corrupt "${key}" in storage, ignoring`)
    return null
  }
}

function _getStoredPermissions() {
  const permissions = _getJSON('permissions')
  return Array.isArray(permissions) ? permissions : undefined
}

/** Map axios/network failures to a user-facing message (no server response body). */
function _apiErrorMessage(error, fallback) {
  const data = error.response?.data
  if (data?.error) return data.error
  if (data && typeof data === 'object') {
    const parts = []
    for (const [field, msgs] of Object.entries(data)) {
      const text = Array.isArray(msgs) ? msgs.join(' ') : String(msgs)
      if (text) parts.push(text)
    }
    if (parts.length) return parts.join(' ')
  }
  if (!error.response) {
    const msg = error.message || ''
    if (msg === 'Network Error' || error.code === 'ERR_NETWORK') {
      return 'Cannot reach the server. Check your connection and try again.'
    }
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.'
    }
  }
  return fallback
}

export const useAuthStore = defineStore('auth', {
  state: () => {
    const storedPermissions = _getStoredPermissions()
    return {
      // Hydrate user/domain from storage SYNCHRONOUSLY. Route components read
      // authStore.user in their setup() (e.g. Dispatch board resolves the user's
      // timezone there), which runs BEFORE App.vue's onMounted->loadFromStorage().
      // With user=null at that point, views silently fell back to the BROWSER
      // timezone — a remote operator with an Edmonton profile got a Taipei board.
      user: _getJSON('user'),
      domain: _getJSON('domain'),
      permissions: storedPermissions,
      // An empty array can be a real, fully-loaded RBAC result. Keep loading state
      // separately so recovery/profile guards do not mistake [] for missing data.
      permissionsHydrated: Array.isArray(storedPermissions),
      // White-label entitlements: enabled feature codes for the current tenant.
      // null = not loaded yet -> treat as "all allowed" so nothing breaks before load.
      features: _getJSON('features'),
      // NOTE: `accessToken` is intentionally NOT state — it is a getter that proxies
      // tokenManager (the single source of truth). After Phase 4.6 the token lives in
      // memory + httpOnly cookie, not JS storage, so a state field seeded from
      // localStorage would be null on reload and break every `authStore.accessToken`
      // reader (raw fetch / WS query token) until a re-login.
      refreshToken: _getItem('refresh_token'),
      // Phase 4.6: the access token no longer lives in JS storage — auth rides the
      // httpOnly cookie. A cached user profile is the reload-survivable signal that
      // a session exists (the cookie is validated server-side on the first call).
      // Native: never treat a cached profile alone as authenticated — it causes
      // entitlements/refresh storms on the login screen that abort password login.
      isAuthenticated: _hasValidSession(),
      // Aug 11 P18: first-login mandatory profile-completion gate.
      // null = not checked yet this session; true/false once /hr/me/profile-status/
      // has answered. Kept in sessionStorage so a reload inside the gate stays gated.
      mustCompleteProfile: _getSessionItem('must_complete_profile') === 'true'
        ? true
        : (_getSessionItem('must_complete_profile') === 'false' ? false : null),
      isGodMode: !!_getSessionItem('god_mode'),
      godModeAdminName: _getSessionItem('god_mode_admin') || '',
      // Platform God Mode (global, cross-domain) session extras
      godModeCompany: _getSessionItem('god_mode_company') || '',
      godModeReadonly: _getSessionItem('god_mode_readonly') === 'true',
      godModeExpiresAt: Number(_getSessionItem('god_mode_expires_at')) || null,
    }
  },

  getters: {
    /**
     * Access token — proxied from tokenManager (single source of truth). Reactive:
     * recomputes after login / silent refresh / rotation because tokenManager holds
     * the token in a Vue ref. Returns null when there is no token yet (auth then
     * rides the httpOnly cookie until the boot silent-refresh populates it).
     */
    accessToken: () => getAccessToken(),

    /**
     * Check if user has a specific permission
     * @param {string} permission - Permission code (e.g., 'parts.view_requests')
     * @returns {boolean}
     *
     * STRICT MODE: Returns false if permissions not loaded
     */
    hasPermission: (state) => (permission) => {
      if (!permission) return true // No permission required
      // STRICT: If permissions not loaded - deny access
      if (!state.permissions || !Array.isArray(state.permissions)) {
        console.warn('[Auth] Permissions not loaded, denying:', permission)
        return false
      }
      // Array of permissions = user needs ANY of them (OR logic)
      if (Array.isArray(permission)) {
        return permission.some(p => permissionMatches(state.permissions, p, state.user))
      }
      return permissionMatches(state.permissions, permission, state.user)
    },

    /**
     * Check if user has any of the given permissions
     * @param {string[]} permissions - Array of permission codes
     * @returns {boolean}
     */
    hasAnyPermission: (state) => (permissions) => {
      if (!permissions || permissions.length === 0) return true
      // STRICT: If permissions not loaded - deny access
      if (!state.permissions || !Array.isArray(state.permissions)) {
        console.warn('[Auth] Permissions not loaded, denying:', permissions)
        return false
      }
      return permissions.some(p => permissionMatches(state.permissions, p, state.user))
    },

    /**
     * Check if user has all of the given permissions
     * @param {string[]} permissions - Array of permission codes
     * @returns {boolean}
     */
    hasAllPermissions: (state) => (permissions) => {
      if (!permissions || permissions.length === 0) return true
      // STRICT: If permissions not loaded - deny access
      if (!state.permissions || !Array.isArray(state.permissions)) {
        console.warn('[Auth] Permissions not loaded, denying:', permissions)
        return false
      }
      return permissions.every(p => permissionMatches(state.permissions, p, state.user))
    },

    /**
     * Jobs workflow mode for the current company/domain.
     * 'multi_job'  = default TechVill behaviour (every stage = new job)
     * 'single_job' = Jobber-like (one job = container with multiple visits)
     * Defaults to 'multi_job' so nothing changes unless explicitly switched.
     * @returns {string}
     */
    jobWorkflowMode: (state) => state.domain?.job_workflow_mode || 'multi_job',

    /** Convenience flag: is this company running the Jobber-style single-job mode? */
    isSingleJobMode: (state) => (state.domain?.job_workflow_mode || 'multi_job') === 'single_job',

    /**
     * Per-company noun for a field worker. Defaults to Technician/Technicians so
     * every existing company is unchanged; a cleaning company sets Cleaner/Cleaners.
     */
    workerTerm: (state) => state.domain?.worker_term_singular || 'Technician',
    workerTermPlural: (state) => state.domain?.worker_term_plural || 'Technicians',

    /**
     * Distance unit for the current company/domain, derived from the company
     * measurement system (domain.units). 'imperial' = miles; anything else
     * (metric/NULL) = kilometers. Drives the useUnits() composable so every
     * distance readout follows the company setting without touching stored
     * values (which stay in kilometres).
     */
    distanceUnit: (state) => (state.domain?.units === 'imperial' ? 'mi' : 'km'),

    /**
     * White-label entitlement check: does THIS tenant have the given feature?
     * Returns true when no feature is required, or when features are not loaded
     * yet (graceful: never hide things before we know). For normal domains the
     * backend returns ALL features, so this is always true.
     * @param {string} code - feature code (e.g. 'voice_robots')
     * @returns {boolean}
     */
    hasFeature: (state) => (code) => {
      if (!code) return true
      if (!Array.isArray(state.features)) return true // not loaded -> allow
      return state.features.includes(code)
    },

    /**
     * Platform HQ company gate. The vendor-only tools (shared-infrastructure
     * monitoring, platform economics, cross-tenant security/audit, tenant
     * onboarding) belong to the SINGLE Platform HQ company (domains.is_platform
     * = 1). They are visible ONLY while the user is working INSIDE that company,
     * and disappear from every client company. This is company-scoped (the
     * CURRENT domain), NOT a per-user flag — so switching/impersonating into a
     * client company hides them. Mirrors backend IsPlatformHQDomain.
     * @returns {boolean}
     */
    isPlatformHQ: (state) => !!state.domain?.is_platform,
  },

  actions: {
    async login(phone_number, password, domain_code = null) {
      try {
        // Build request data - don't send domain_code if it's null
        const requestData = {
          phone_number,
          password
        }
        if (domain_code) {
          requestData.domain_code = domain_code
        }

        const response = await apiClient.post('/auth/login/', requestData)

        const data = response.data

        // Check if domain selection needed (multi-domain response)
        if (data.domains && data.domains.length > 1) {
          return { needsDomainSelection: true, domains: data.domains, user_id: data.user_id }
        }

        // Check if login was successful (single domain response)
        if (data.access_token && data.user && data.domain) {
          // Store tokens and user info
          this.setAuthData(data)
          return {
            success: true,
            requires_password_change: data.requires_password_change || false
          }
        }

        // Unexpected response
        throw new Error('Invalid login response')
      } catch (error) {
        console.error('Login error:', error)
        throw error
      }
    },

    /**
     * Passwordless login via SMS code (Twilio Verify).
     * Step 1: verify with { phone_number, code }.
     * Multi-domain users get a domain list + a short-lived login_ticket so the
     * domain choice does NOT need the (already consumed) code again — step 2
     * calls this with { phone_number, domain_code, login_ticket }.
     */
    async loginWithCode(phone_number, code = null, domain_code = null, login_ticket = null) {
      try {
        const requestData = { phone_number }
        if (code) requestData.code = code
        if (domain_code) requestData.domain_code = domain_code
        if (login_ticket) requestData.login_ticket = login_ticket

        const response = await apiClient.post('/auth/login/verify-code/', requestData)
        const data = response.data

        if (data.domains && data.domains.length > 1 && !data.access_token) {
          return {
            needsDomainSelection: true,
            domains: data.domains,
            user_id: data.user_id,
            login_ticket: data.login_ticket,
          }
        }

        if (data.access_token && data.user && data.domain) {
          this.setAuthData(data)
          return {
            success: true,
            requires_password_change: data.requires_password_change || false,
          }
        }

        throw new Error('Invalid login response')
      } catch (error) {
        console.error('Code login error:', error)
        throw error
      }
    },

    async register(userData) {
      try {
        const response = await apiClient.post('/auth/register', userData)
        const data = response.data

        this.setAuthData(data)

        return { success: true }
      } catch (error) {
        console.error('Registration error:', error)
        throw error
      }
    },

    async technicianLogin(phone_number, password, domain_code = null) {
      try {
        const requestData = { phone_number, password }
        if (domain_code) {
          requestData.domain_code = domain_code
        }

        const response = await axios.post(TECH_LOGIN_URL, requestData, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 60000,
        })
        const data = response.data

        // Check for errors
        if (data.error) {
          return { error: data.error }
        }

        // Multi-domain technician: backend returns a company list instead of
        // tokens — the login screen must show the domain selector first.
        if (data.domains && data.domains.length > 1 && !data.access_token) {
          console.log('[TechLogin] multi-domain user, showing domain selection:', data.domains.length)
          this.isAuthenticated = false
          return { needsDomainSelection: true, domains: data.domains, user_id: data.user_id }
        }

        // Single-domain (or second step with domain_code): full auth payload required.
        if (data.access_token && data.user && data.domain) {
          this.setAuthData(data)
          if (isNative() && data.refresh_token) {
            try {
              await persistRefreshToken(data.refresh_token)
            } catch (error) {
              // The access token and in-memory session are already live. A
              // secure-storage write failure must not turn success into a
              // contradictory logged-out state with a usable token.
              console.warn('[TechLogin] Could not persist refresh token:', error?.message || error)
            }
            try {
              const { getBiometricConfiguredPhone } = await import('@/native/biometricEnrollment')
              if (getBiometricConfiguredPhone()) {
                const { saveBiometricRefreshToken } = await import('@/native/biometric')
                await saveBiometricRefreshToken(data.refresh_token)
              }
            } catch { /* ignore */ }
          }

          _setTechnicianSession(data.interface_version)
          _primeTechInterface(data)

          console.log('✅ Technician login successful:', {
            user: data.user?.email,
            domain: data.domain?.name,
            interface: data.interface_name,
          })

          return {
            success: true,
            is_technician: true,
            interface_version: data.interface_version,
            interface_name: data.interface_name,
          }
        }

        this.isAuthenticated = false
        return { error: 'Unexpected login response. Please try again.' }
      } catch (error) {
        this.isAuthenticated = false
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
          return { error: 'Login interrupted. Please try again.' }
        }
        console.error(
          'Technician login error:',
          error?.response?.status,
          error?.response?.data,
          error?.message,
        )
        return {
          error: _apiErrorMessage(
            error,
            'Sign in failed. Please check your phone number and password and try again.',
          ),
        }
      }
    },

    /**
     * Restore user/domain after biometric refresh when localStorage profile was purged.
     * @returns {Promise<boolean>}
     */
    async hydrateTechnicianProfileAfterUnlock() {
      this.loadFromStorage()
      if (this.user && this.domain) return true

      if (!this.user) {
        try {
          await this.refreshUser()
        } catch (e) {
          console.warn('[TechLogin] refreshUser after biometric unlock failed:', e?.message || e)
        }
        this.loadFromStorage()
      }

      if (this.user && this.domain) return true

      if (this.user && !this.domain) {
        try {
          const { data: domainRows } = await apiClient.get('/auth/domains/')
          const domains = (Array.isArray(domainRows) ? domainRows : domainRows?.results || [])
            .map((d) => ({
              id: d.id,
              name: d.name,
              code: d.code,
              is_platform: !!d.is_platform,
            }))
            .filter((d) => d.id && d.code)
          if (domains.length === 1) {
            const result = await this.technicianSelectDomain(domains[0].code)
            return !result?.error && !!this.user && !!this.domain
          }
          if (domains.length > 1) return true
          const domainId = this.user?.current_domain_id
          if (domainId) {
            const match = domains.find((d) => d.id === domainId)
            if (match) {
              const result = await this.technicianSelectDomain(match.code)
              return !result?.error && !!this.user && !!this.domain
            }
            try {
              await this.refreshDomain(domainId)
              this.loadFromStorage()
              if (this.domain) return true
            } catch (e) {
              console.warn('[TechLogin] refreshDomain after biometric unlock failed:', e?.message || e)
            }
          }
        } catch (e) {
          console.warn('[TechLogin] domain restore after biometric unlock failed:', e?.message || e)
        }
      }

      return !!(this.user && this.domain)
    },

    /**
     * Technician biometric quick login — refresh token + biometric gate (no password).
     * Proves the user; if they belong to 2+ companies, returns domain picker data.
     */
    async technicianBiometricTokenLogin() {
      try {
        const { ensureRefreshTokenForBiometricUnlock } = await import('@/native/biometric')
        const rt = await ensureRefreshTokenForBiometricUnlock()
        if (!rt) {
          return { error: 'No saved session. Please sign in with your password.' }
        }

        _setTechnicianSession()

        const token = await refreshAccessToken(axios)
        if (!token) {
          return { error: 'Session expired. Please sign in with your password.' }
        }

        const hydrated = await this.hydrateTechnicianProfileAfterUnlock()
        if (!hydrated) {
          return { error: 'No saved session. Please sign in with your password.' }
        }

        try { await this.refreshUser() } catch { /* keep hydrated profile */ }
        _setTechnicianSession()

        if (isNative()) {
          const { markIdentityVerified } = await import('@/native/identityGate')
          await markIdentityVerified()
        }

        // Company is not bound to the biometric unlock — ask when multi-domain.
        // Keep tokens in storage for /auth/domains/ and select-domain, but do not
        // mark the session authenticated yet (router would skip the picker).
        try {
          const { data: domainRows } = await apiClient.get('/auth/domains/')
          const domains = (Array.isArray(domainRows) ? domainRows : domainRows?.results || [])
            .map((d) => ({
              id: d.id,
              name: d.name,
              code: d.code,
              is_platform: !!d.is_platform,
            }))
            .filter((d) => d.id && d.code)
          if (domains.length > 1) {
            this.isAuthenticated = false
            console.log('[TechLogin] biometric token unlock — domain selection:', domains.length)
            return {
              needsDomainSelection: true,
              domains,
              authMode: 'token',
              user_id: this.user?.id,
            }
          }
        } catch (e) {
          console.warn('[TechLogin] domain list after biometric unlock failed:', e?.message || e)
        }

        this.isAuthenticated = true
        console.log('✅ Technician biometric token login successful:', {
          user: this.user?.email,
          domain: this.domain?.name,
        })
        return {
          success: true,
          is_technician: true,
          interface_version: localStorage.getItem('interface_version_id') || '',
        }
      } catch (error) {
        console.error('Technician biometric token login error:', error)
        return {
          error: _apiErrorMessage(error, 'Session expired. Please sign in with your password.'),
        }
      }
    },

    /**
     * After biometric/token unlock: issue session for the chosen company.
     */
    async technicianSelectDomain(domain_code) {
      try {
        const response = await apiClient.post('/auth/technician-select-domain/', {
          domain_code,
        })
        const data = response.data
        if (data?.error) {
          return { error: data.error }
        }
        if (!(data?.access_token && data?.user && data?.domain)) {
          return { error: 'Unexpected response. Please try again.' }
        }
        this.setAuthData(data)
        _setTechnicianSession(data.interface_version)
        _primeTechInterface(data)
        if (isNative()) {
          const { markIdentityVerified } = await import('@/native/identityGate')
          await markIdentityVerified()
        }
        return {
          success: true,
          is_technician: true,
          interface_version: data.interface_version,
          interface_name: data.interface_name,
        }
      } catch (error) {
        return {
          error: _apiErrorMessage(error, 'Could not switch company. Please try again.'),
        }
      }
    },

    /**
     * Office in-app company switch (no re-login). Issues a fresh session for the
     * chosen company the user is already a member of and swaps tenant context.
     */
    async selectDomain(domain_code) {
      try {
        const response = await apiClient.post('/auth/select-domain/', { domain_code })
        const data = response.data
        if (data?.error) {
          return { error: data.error }
        }
        if (!(data?.access_token && data?.user && data?.domain)) {
          return { error: 'Unexpected response. Please try again.' }
        }
        this.setAuthData(data)
        return { success: true, domain: data.domain }
      } catch (error) {
        return {
          error: _apiErrorMessage(error, 'Could not switch company. Please try again.'),
        }
      }
    },

    /**
     * Technician passkey login (native — WebAuthn platform authenticator).
     * Passkey proves the user; multi-domain users get a company picker next.
     */
    async technicianPasskeyLogin(phone_number) {
      try {
        const { loginWithPasskey } = await import('@/native/passkey')
        const result = await loginWithPasskey(phone_number)
        if (!result.ok) {
          return { error: result.error || 'Passkey login failed' }
        }
        const data = result.data
        if (data?.error) {
          return { error: data.error }
        }

        // Multi-company: identity verified, no tokens yet.
        if (data?.domains && data.domains.length > 1 && !data.access_token) {
          this.isAuthenticated = false
          return {
            needsDomainSelection: true,
            domains: data.domains,
            user_id: data.user_id,
            passkey_session_token: data.passkey_session_token,
            authMode: 'passkey',
            phone: phone_number,
          }
        }

        this.setAuthData(data)
        _setTechnicianSession(data.interface_version)
        _primeTechInterface(data)
        if (isNative()) {
          const { markIdentityVerified } = await import('@/native/identityGate')
          await markIdentityVerified()
        }
        console.log('✅ Technician passkey login successful:', {
          user: data.user?.email,
          domain: data.domain?.name,
        })
        return {
          success: true,
          is_technician: true,
          interface_version: data.interface_version,
          interface_name: data.interface_name,
        }
      } catch (error) {
        console.error('Technician passkey login error:', error)
        return {
          error: _apiErrorMessage(error, 'Passkey login failed.'),
        }
      }
    },

    /**
     * Complete passkey login after company pick (uses short-lived session ticket).
     */
    async technicianPasskeySelectDomain(phone_number, passkey_session_token, domain_code) {
      try {
        const { completePasskeyDomainLogin } = await import('@/native/passkey')
        const result = await completePasskeyDomainLogin(
          phone_number,
          passkey_session_token,
          domain_code,
        )
        if (!result.ok) {
          return { error: result.error || 'Passkey login failed' }
        }
        const data = result.data
        if (data?.error) {
          return { error: data.error }
        }
        if (!(data?.access_token && data?.user && data?.domain)) {
          return { error: 'Unexpected response. Please try again.' }
        }
        this.setAuthData(data)
        _setTechnicianSession(data.interface_version)
        _primeTechInterface(data)
        if (isNative()) {
          const { markIdentityVerified } = await import('@/native/identityGate')
          await markIdentityVerified()
        }
        return {
          success: true,
          is_technician: true,
          interface_version: data.interface_version,
          interface_name: data.interface_name,
        }
      } catch (error) {
        return {
          error: _apiErrorMessage(error, 'Passkey login failed.'),
        }
      }
    },

    /**
     * Check if current session is a technician session
     */
    isTechnicianSession() {
      return _getLocalItem('is_technician_session') === 'true'
    },

    setAuthData(data) {
      const identityChanged = Boolean(
        (this.user?.id && data.user?.id && this.user.id !== data.user.id)
        || (this.domain?.id && data.domain?.id && this.domain.id !== data.domain.id)
      )
      if (identityChanged) {
        // Company/account switches remain authenticated, so the App.vue logout
        // watcher cannot invalidate old repair work. Abort tenant-A requests
        // before installing tenant-B state and advance the repair generation.
        abortAllRequests()
        resetNativeSessionRights()
      }
      const store = _storage()
      // accessToken is a getter (tokenManager) — set via setAccessToken below.
      this.refreshToken = data.refresh_token
      this.user = data.user
      this.domain = data.domain
      this.permissions = Array.isArray(data.permissions) ? data.permissions : []
      this.permissionsHydrated = Array.isArray(data.permissions)

      setAccessToken(data.access_token)
      this.isAuthenticated = !!getAccessToken() && !!data.user && !!data.domain
      // Arm proactive refresh so the short-lived (15-min) access token is renewed
      // BEFORE it expires — prevents the idle→pickup 401 storm that was logging
      // operators out. No-op in God Mode / when expires_in is absent.
      scheduleProactiveRefresh(data.expires_in)
      // Phase 4.6: token persistence is mode-aware (backend signals via cookie_auth).
      if (isNative()) {
        // After login, re-read Capgo channel from the API the shell talks to and
        // pull any pending OTA (DEV banner / UI fixes). Boot only refreshes when
        // a session already existed — fresh login previously skipped this.
        import('@/native/capgoOta').then(({ refreshCapgoOtaConfig }) => {
          refreshCapgoOtaConfig().catch((e) => {
            console.warn('[auth] Capgo OTA refresh after login failed:', e?.message || e)
          })
        }).catch(() => {})
        // Native cross-site: persist refresh token in secure storage (the httpOnly
        // cookie is never sent) and use the body-refresh path. Do NOT mirror it to
        // localStorage on native.
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token)
          import('@/native/biometricEnrollment').then(({ getBiometricConfiguredPhone }) => {
            if (getBiometricConfiguredPhone()) {
              import('@/native/biometric').then(({ saveBiometricRefreshToken }) => {
                saveBiometricRefreshToken(data.refresh_token).catch(() => {})
              })
            }
          }).catch(() => {})
        }
      } else if (data.cookie_auth) {
        //  - cookie session (dual/strict): the httpOnly cookie carries auth → keep
        //    NOTHING in JS-readable storage (purge any legacy mirror). XSS-proof.
        purgeLegacyStoredTokens()
      } else {
        //  - legacy session (no cookie): keep the body-token mirror in localStorage
        //    so reload/offline keeps working exactly as before — zero regression.
        try {
          store.setItem('access_token', data.access_token)
          if (data.refresh_token) store.setItem('refresh_token', data.refresh_token)
        } catch (error) {
          console.warn('[Auth] Could not persist legacy tokens:', error?.message || error)
        }
      }
      try {
        // Remove old rights first. A partial storage write may leave identity
        // incomplete, but it can never attach tenant A's ACL to tenant B.
        store.removeItem('permissions')
        store.setItem('user', JSON.stringify(data.user))
        store.setItem('domain', JSON.stringify(data.domain))
        if (this.permissionsHydrated) {
          store.setItem('permissions', JSON.stringify(this.permissions))
        }
      } catch (error) {
        console.warn('[Auth] Could not persist auth context:', error?.message || error)
      }

      // Load white-label entitlements (non-blocking; no-op for normal domains).
      if (this.isAuthenticated) this.loadFeatures()

      if (!this.isGodMode) {
        // Do not wipe technician session during native profile refresh (biometric unlock).
        const keepTechnician = isNative() && _getLocalItem('is_technician_session') === 'true'
        if (!keepTechnician) {
          try {
            localStorage.removeItem('is_technician_session')
            localStorage.removeItem('interface_version_id')
          } catch { /* unavailable storage must not invalidate an in-memory login */ }
        }
      }

      console.log('[Auth] Data saved:', {
        user: data.user.email,
        domain: data.domain.name,
        godMode: this.isGodMode,
        permissions_count: this.permissions.length,
      })

      // Company context just (re)established → make the WHOLE UI reflect the
      // new tenant immediately (logo, colors, name, favicon) WITHOUT a hard
      // reload, and drop cached API responses from the previous company so no
      // stale data bleeds across tenants. God Mode tabs handle their own
      // branding + full reload in ImpersonateView, so skip them here.
      if (!this.isGodMode) {
        this.refreshTenantContext()
      }
    },

    /**
     * Refresh everything that is tied to the active company after a login /
     * domain switch: purge cross-tenant caches and reload branding so the UI
     * repaints for the new tenant instantly. Non-blocking and best-effort —
     * a failure here must never break the login flow.
     */
    async refreshTenantContext() {
      // 1. Purge the Service Worker api-cache (GET responses cached for the
      //    PREVIOUS company would otherwise leak into this session).
      try {
        if (typeof caches !== 'undefined' && caches?.delete) {
          await caches.delete('api-cache')
        }
      } catch (e) { /* never block login */ }

      // 2. Reset the per-session brand slot cache (company-specific HTML).
      try { if (window.__brandSlotsCache) window.__brandSlotsCache = {} } catch (e) {}

      // 3. Reload branding (logo/colors/name/favicon) for the active domain,
      //    then reapply the user's personal custom theme on top of it.
      try {
        const { useBrandingStore } = await import('@/stores/branding')
        const branding = useBrandingStore()
        // Apply the account's per-user look (icon set / button skin / personal
        // theme) from the `user` cache that login just wrote — SYNCHRONOUSLY,
        // BEFORE the async /current/ round-trip. The branding store is a
        // singleton created once at boot; on the /login page it was built with
        // NO user cache, so its iconStyle defaulted to `color3d`. Login writes
        // the cache but never reconciled the live store, so the sidebar showed
        // the default 3D icons until the network settled (~2 s). Reading the
        // fresh cache here flips iconStyle to the saved style (e.g. `paper`) on
        // the first authenticated frame. syncAccountPrefs() also refreshes the
        // icon_style_v1 mirror + <html data-icon-style>, so later reloads paint
        // correctly on frame one too.
        try { branding.syncAccountPrefs() } catch (e) { /* best-effort */ }
        await branding.loadCurrent()
        try {
          const { useCustomThemesStore } = await import('@/stores/customThemes')
          const custom = useCustomThemesStore()
          await custom.load()
          custom.applyActive(branding.effectiveMode)
        } catch (e) { /* personal theme optional */ }
      } catch (e) {
        console.warn('[Auth] Tenant branding refresh failed:', e?.message || e)
      }

      // 4. Company context (tv_club_enabled, Flow flags, etc.) is keyed to the
      //    active domain — purge and refetch so a switch never shows the prior
      //    tenant's module toggles.
      try {
        const { useCompanyContext } = await import('@/composables/useCompanyContext')
        const companyCtx = useCompanyContext()
        companyCtx.reset()
        await companyCtx.load(true)
      } catch (e) {
        console.warn('[Auth] Company context refresh failed:', e?.message || e)
      }
    },

    setGodMode(adminName, options = {}) {
      sessionStorage.setItem('god_mode', 'true')
      sessionStorage.setItem('god_mode_admin', adminName)
      this.isGodMode = true
      this.godModeAdminName = adminName
      // Platform God Mode extras (optional — domain-level god mode unaffected)
      if (options.company) {
        sessionStorage.setItem('god_mode_company', options.company)
        this.godModeCompany = options.company
      }
      if (options.readonly) {
        sessionStorage.setItem('god_mode_readonly', 'true')
        this.godModeReadonly = true
      }
      if (options.expiresIn) {
        const expiresAt = Date.now() + options.expiresIn * 1000
        sessionStorage.setItem('god_mode_expires_at', String(expiresAt))
        this.godModeExpiresAt = expiresAt
      }
    },

    exitGodMode() {
      sessionStorage.clear()
      window.close()
    },

    /**
     * Native-only: lock the app while keeping refresh token + cached profile so
     * biometric quick login works on the login screen (Sign Out with biometrics on).
     */
    async lockForBiometricLogin() {
      await loadStoredTokens()
      const lastPhone = this.user?.phone_number || null
      abortAllRequests()
      clearAccessToken()
      this.user = null
      this.domain = null
      this.permissions = []
      this.permissionsHydrated = false
      this.features = null
      this.isAuthenticated = false
      this.isGodMode = false
      let rt = getRefreshToken()
      if (!rt) {
        try { rt = await secureGet('refresh_token') } catch { /* ignore */ }
      }
      if (!rt) {
        try {
          const { getBiometricRefreshToken, saveBiometricRefreshToken } = await import('@/native/biometric')
          rt = await getBiometricRefreshToken()
          if (rt) await saveBiometricRefreshToken(rt)
        } catch { /* ignore */ }
      } else {
        try {
          const { saveBiometricRefreshToken } = await import('@/native/biometric')
          await saveBiometricRefreshToken(rt)
        } catch { /* ignore */ }
      }
      if (rt) {
        setRefreshToken(rt)
        this.refreshToken = rt
        await persistRefreshToken(rt)
      }
      if (_getLocalItem('user')) {
        _setTechnicianSession()
      }
      if (lastPhone) setLastLoginPhone(lastPhone)
      console.log('[Auth] App locked — biometric re-login available')
    },

    async logout(logoutAll = false) {
      console.log('[Auth] logout() called, logoutAll:', logoutAll)
      // Native + biometrics: Sign Out locks the app instead of revoking refresh.
      if (isNative() && !logoutAll) {
        try {
          await loadStoredTokens()
          const { getBiometricConfiguredPhone } = await import('@/native/biometricEnrollment')
          const { getTokenQuickLoginPhone, getBiometricRefreshToken } = await import('@/native/biometric')
          const configured = getBiometricConfiguredPhone()
          const tokenPhone = await getTokenQuickLoginPhone()
          const bioRefresh = await getBiometricRefreshToken()
          if (configured || tokenPhone || bioRefresh) {
            await this.lockForBiometricLogin()
            return
          }
        } catch { /* fall through to full logout */ }
      }

      const lastPhone = this.user?.phone_number || null
      abortAllRequests()
      try {
        await apiClient.post('/auth/logout/', {
          refresh_token: this.refreshToken,
          logout_all: logoutAll
        })
        console.log('[Auth] Server logout successful')
      } catch (error) {
        console.error('[Auth] Logout API error:', error?.response?.status, error?.message)
      } finally {
        if (lastPhone) setLastLoginPhone(lastPhone)
        this.clearAuth()
      }
    },

    clearAuth() {
      this.user = null
      this.domain = null
      this.permissions = []
      this.permissionsHydrated = false
      // accessToken is a getter (tokenManager) — cleared via clearAccessToken below.
      this.refreshToken = null
      this.isAuthenticated = false
      this.isGodMode = false
      this.godModeAdminName = ''
      this.features = null
      this.mustCompleteProfile = null
      try { localStorage.removeItem('features'); sessionStorage.removeItem('features') } catch (e) {}
      try { sessionStorage.removeItem('must_complete_profile') } catch (e) {}

      // Clear only auth-related keys; preserve offline caches (fsa_*), dialer state, etc.
      clearAccessToken()
      clearRefreshToken() // native secure storage + web local/session
      clearAuthStorage()
      sessionStorage.removeItem('god_mode')
      sessionStorage.removeItem('god_mode_admin')

      // Wipe the previous tenant's branding + cached API data so NOTHING from
      // the old company shows after logout (logo, colors, cached lists, brand
      // slots). The login page repaints neutral/public branding by subdomain.
      // Best-effort + fire-and-forget — must never block logout.
      try {
        if (typeof caches !== 'undefined' && caches?.delete) caches.delete('api-cache').catch(() => {})
      } catch (e) {}
      try { if (window.__brandSlotsCache) window.__brandSlotsCache = {} } catch (e) {}
      import('@/stores/branding')
        .then(({ useBrandingStore }) => { try { useBrandingStore().reset() } catch (e) {} })
        .catch(() => {})

      console.log('[Auth] clearAuth complete — auth keys removed, branding reset, offline data preserved')
    },

    /**
     * Drop a cached native profile that has no tokens (failed refresh / partial
     * login). Prevents background entitlements calls on the login screen.
     *
     * Preserves the refresh token when token-gated biometric unlock is configured
     * (phone hint in secure storage) so the login screen can still offer biometrics
     * after an app restart. Explicit Sign Out with biometrics enabled locks the app
     * (refresh + profile kept) instead of revoking.
     *
     * Must run AFTER the boot gate: Capgo OTA reloads the WebView with no
     * in-memory access token, and clearing Keychain here before silent refresh
     * finishes forced technicians back through password login.
     */
    async purgeZombieSessionIfNeeded() {
      if (!isNative()) return
      try { await awaitBootGate() } catch { /* never block login mount */ }
      if (_hasValidSession()) return

      let preserveBiometric = false
      try {
        const { shouldPreserveRefreshForBiometric } = await import('@/native/biometricLoginStatus')
        preserveBiometric = await shouldPreserveRefreshForBiometric()
      } catch { /* ignore */ }

      const hasCachedProfile = !!(
        _getItem('user') ||
        localStorage.getItem('is_technician_session') === 'true'
      )
      if (preserveBiometric) {
        try {
          const { ensureRefreshTokenForBiometricUnlock } = await import('@/native/biometric')
          await ensureRefreshTokenForBiometricUnlock()
        } catch { /* ignore */ }
      }
      const hasStaleRefresh = !!(getRefreshToken() || (preserveBiometric && await (async () => {
        try {
          const { getBiometricRefreshToken } = await import('@/native/biometric')
          return await getBiometricRefreshToken()
        } catch { return null }
      })()))

      if (preserveBiometric && hasStaleRefresh) {
        console.log('[Auth] Biometric lock profile kept on login screen')
        if (_getLocalItem('user')) {
          _setTechnicianSession()
        }
        this.user = null
        this.domain = null
        this.permissions = []
        this.permissionsHydrated = false
        this.features = null
        this.isAuthenticated = false
        return
      }

      if (preserveBiometric && hasCachedProfile) {
        console.log('[Auth] Biometric configured — keeping cached profile on login screen')
        _setTechnicianSession()
        this.user = null
        this.domain = null
        this.permissions = []
        this.permissionsHydrated = false
        this.features = null
        this.isAuthenticated = false
        return
      }

      if (!hasCachedProfile && !hasStaleRefresh) return

      // After boot: a surviving refresh token is still usable (OTA reload race or
      // transient network). bootstrapSession already removed 401/403-dead tokens.
      if (!shouldClearNativeRefreshOnLoginPurge({
        hasAccessToken: !!getAccessToken(),
        hasRefreshToken: hasStaleRefresh,
        preserveBiometric,
      })) {
        console.log('[Auth] Refresh present after boot without access token — keeping Keychain')
        this.isAuthenticated = false
        return
      }

      console.log('[Auth] Clearing zombie native session (no access token)')
      this.user = null
      this.domain = null
      this.permissions = []
      this.permissionsHydrated = false
      this.features = null
      this.isAuthenticated = false
      localStorage.removeItem('user')
      localStorage.removeItem('domain')
      localStorage.removeItem('permissions')
      localStorage.removeItem('features')
      localStorage.removeItem('is_technician_session')
      localStorage.removeItem('interface_version_id')
    },

    loadFromStorage() {
      this.isGodMode = !!sessionStorage.getItem('god_mode')
      this.godModeAdminName = sessionStorage.getItem('god_mode_admin') || ''
      this.godModeCompany = sessionStorage.getItem('god_mode_company') || ''
      this.godModeReadonly = sessionStorage.getItem('god_mode_readonly') === 'true'
      this.godModeExpiresAt = Number(sessionStorage.getItem('god_mode_expires_at')) || null

      // Native: the refresh token is in secure storage, not localStorage. Pull
      // it into state so logout() can revoke the right token (requires
      // loadStoredTokens() to have warmed the cache at startup).
      if (isNative()) {
        const rt = getRefreshToken()
        if (rt) this.refreshToken = rt
      }

      const storedUser = _getJSON('user')
      const storedDomain = _getJSON('domain')
      const liveUserId = this.user?.id
      const liveDomainId = this.domain?.id
      const cacheMatchesLiveIdentity = Boolean(
        (!liveUserId || !storedUser?.id || liveUserId === storedUser.id)
        && (!liveDomainId || !storedDomain?.id || liveDomainId === storedDomain.id)
      )
      if (cacheMatchesLiveIdentity) {
        if (storedUser) this.user = storedUser
        if (storedDomain) this.domain = storedDomain
      } else {
        console.warn('[Auth] Ignoring auth cache from a different identity')
      }
      const permissionsRead = _readStorageItem('permissions')
      let storedPermissions
      if (permissionsRead.value) {
        try { storedPermissions = JSON.parse(permissionsRead.value) } catch { /* corrupt cache */ }
      }
      const identityMatches = cacheMatchesLiveIdentity && Boolean(
        storedUser?.id && storedDomain?.id
        && this.user?.id === storedUser.id
        && this.domain?.id === storedDomain.id
      )

      if (permissionsRead.available && Array.isArray(storedPermissions) && identityMatches) {
        this.permissions = storedPermissions
        this.permissionsHydrated = true
        console.log('[Auth] Loaded permissions:', {
          count: this.permissions?.length,
          godMode: this.isGodMode,
        })
      } else if (!permissionsRead.available) {
        console.warn('[Auth] Permission storage unavailable — keeping in-memory rights')
      } else if (!this.permissionsHydrated) {
        console.warn('[Auth] No permissions in storage!')
        this.permissions = []
        this.permissionsHydrated = false
      }

      const featuresStr = _getItem('features')
      if (featuresStr) {
        try { this.features = JSON.parse(featuresStr) } catch (e) { this.features = null }
      }
      this.isAuthenticated = _hasValidSession()
      // Refresh entitlements from server in the background (no-op for normal domains).
      if (this.isAuthenticated) this.loadFeatures()
    },

    /**
     * Load white-label entitlements for the current tenant.
     * For normal domains the backend returns ALL features, so gating is a no-op.
     * Failures are non-fatal: we keep features=null which means "allow everything".
     */
    async loadFeatures() {
      try {
        const response = await apiClient.get('/entitlements/me/')
        const features = Array.isArray(response.data?.features) ? response.data.features : null
        this.features = features
        if (features) _storage().setItem('features', JSON.stringify(features))
        console.log('[Auth] Entitlements loaded:', {
          enforced: response.data?.enforced,
          count: features?.length,
        })
        return features
      } catch (error) {
        console.warn('[Auth] Failed to load entitlements (allowing all):', error?.message || error)
        return null
      }
    },

    async refreshUser() {
      try {
        const response = await apiClient.get('/auth/me/')
        this.user = response.data
        try {
          _storage().setItem('user', JSON.stringify(response.data))
        } catch (error) {
          console.warn('[Auth] Could not persist refreshed user:', error?.message || error)
        }
        // Harden the fast anti-FOUC source for the menu icon set from the
        // AUTHORITATIVE server profile: seed the icon_style mirror + <html>
        // signal so the NEXT page load paints the SAVED icon style on the very
        // first frame (no default->saved flash). Only seed when the server
        // actually provides a value; never overwrite the mirror with a guess.
        try {
          const iconStyle = response.data && response.data.ui_preferences
            ? response.data.ui_preferences.icon_style
            : null
          if (iconStyle) {
            localStorage.setItem('icon_style_v1', iconStyle)
            document.documentElement.dataset.iconStyle = iconStyle
          }
        } catch (e) { /* storage/DOM unavailable — ignore */ }
        return response.data
      } catch (error) {
        console.error('Error refreshing user:', error)
        throw error
      }
    },

    // Aug 11 P18: ask the backend whether the current user must finish their
    // self-service profile before using the app. Result is cached in
    // sessionStorage; call force=true after the user saves their profile.
    async checkProfileCompletion(force = false) {
      if (!this.isAuthenticated) {
        this.mustCompleteProfile = false
        return false
      }
      if (!force && this.mustCompleteProfile !== null) {
        return this.mustCompleteProfile
      }
      try {
        const res = await apiClient.get('/hr/me/profile-status/')
        const must = !!res.data?.must_complete_profile
        this.mustCompleteProfile = must
        try { sessionStorage.setItem('must_complete_profile', must ? 'true' : 'false') } catch (e) { /* ignore */ }
        return must
      } catch (error) {
        // Never hard-block on a status error (e.g. no HR access) — fail open.
        console.warn('[Auth] profile-status check failed', error?.response?.status)
        this.mustCompleteProfile = false
        try { sessionStorage.setItem('must_complete_profile', 'false') } catch (e) { /* ignore */ }
        return false
      }
    },

    clearProfileGate() {
      this.mustCompleteProfile = false
      try { sessionStorage.setItem('must_complete_profile', 'false') } catch (e) { /* ignore */ }
    },

    async refreshDomain(domainId) {
      try {
        const response = await apiClient.get(`/auth/domains/${domainId}/settings/`)
        // Merge with existing domain data to preserve id and other fields
        const updatedDomain = {
          id: this.domain?.id || domainId,
          ...this.domain,
          ...response.data
        }
        this.domain = updatedDomain
        try {
          _storage().setItem('domain', JSON.stringify(updatedDomain))
        } catch (error) {
          console.warn('[Auth] Could not persist refreshed domain:', error?.message || error)
        }
        return response.data
      } catch (error) {
        console.error('Error refreshing domain:', error)
        throw error
      }
    },

    updateUser(userData) {
      this.user = { ...this.user, ...userData }
      _storage().setItem('user', JSON.stringify(this.user))
    },

    /**
     * Update a single key in the user's ui_preferences JSON.
     * Applied locally at once (so the UI reacts instantly), then persisted via
     * PATCH /auth/me/ and reconciled with whatever the server actually stored.
     *
     * TICK-00188: a failed PATCH used to keep the optimistic value, leaving the
     * browser permanently ahead of the account — every later reload painted the
     * cached look, then snapped back to the stored one as soon as /auth/me
     * answered. The change is reverted instead, so what is on screen is always
     * what is saved.
     */
    async updateUiPreference(key, value) {
      const current = (this.user && this.user.ui_preferences) || {}
      const hadKey = Object.prototype.hasOwnProperty.call(current, key)
      const previous = current[key]
      this._setUiPreferences({ ...current, [key]: value })
      try {
        // Send ONLY the changed key. The server MERGES it into the stored JSON,
        // so a stale local cache can never clobber preferences saved on another
        // device. DB remains the single source of truth.
        const { data } = await apiClient.patch('/auth/me/', { ui_preferences: { [key]: value } })
        // Reconcile only the key this call owns. Replacing the whole object
        // with the response would let an earlier-sent, later-resolving PATCH
        // clobber a different key a concurrent updateUiPreference() call has
        // since applied (or already confirmed) locally.
        const serverValue = data && data.ui_preferences && typeof data.ui_preferences === 'object'
          ? data.ui_preferences[key]
          : value
        const latest = (this.user && this.user.ui_preferences) || {}
        this._setUiPreferences({ ...latest, [key]: serverValue })
      } catch (e) {
        const reverted = { ...((this.user && this.user.ui_preferences) || {}) }
        if (hadKey) reverted[key] = previous
        else delete reverted[key]
        this._setUiPreferences(reverted)
        console.warn('[Auth] Failed to persist ui_preferences to server:', e?.message || e)
      }
    },

    /** Replace ui_preferences in state + storage cache in one place. */
    _setUiPreferences(prefs) {
      this.user = { ...this.user, ui_preferences: prefs }
      _storage().setItem('user', JSON.stringify(this.user))
    },

    updateDomain(domainData) {
      this.domain = { ...this.domain, ...domainData }
      _storage().setItem('domain', JSON.stringify(this.domain))
    },

    /**
     * Refresh permissions from server without re-login
     * Call this after admin changes permissions
     */
    async refreshPermissions() {
      try {
        if (!this.isAuthenticated || !this.domain?.id) {
          console.warn('[Auth] Cannot refresh permissions - not authenticated or no domain')
          return null
        }

        console.log('[Auth] Refreshing permissions from server...')

        const response = await apiClient.get('/auth/permissions/refresh/', {
          headers: {
            'X-Domain-ID': this.domain.id
          }
        })

        const newPermissions = response.data?.permissions
        if (!Array.isArray(newPermissions)) {
          throw new Error('Permissions refresh returned an invalid payload')
        }
        const oldCount = this.permissions?.length || 0

        this.permissions = newPermissions
        this.permissionsHydrated = true
        try {
          _storage().setItem('permissions', JSON.stringify(newPermissions))
        } catch (error) {
          console.warn('[Auth] Could not persist refreshed permissions:', error?.message || error)
        }

        // Refresh user's team (org_unit) so team-based UI (Take/Complete/Edit
        // buttons on team tasks) works without a full re-login.
        const newOrgUnitId = response.data.org_unit_id ?? null
        const newOrgUnitName = response.data.org_unit_name ?? null
        if (this.user) {
          const prevOu = this.user.org_unit_id ?? null
          this.user = {
            ...this.user,
            org_unit_id: newOrgUnitId,
            org_unit_name: newOrgUnitName,
          }
          try {
            _storage().setItem('user', JSON.stringify(this.user))
          } catch (error) {
            console.warn('[Auth] Could not persist refreshed user:', error?.message || error)
          }
          if (prevOu !== newOrgUnitId) {
            console.log(`[Auth] org_unit_id updated: ${prevOu} -> ${newOrgUnitId} (${newOrgUnitName})`)
          }
        }

        console.log(`✅ [Auth] Permissions refreshed: ${oldCount} -> ${newPermissions.length}`,
                    newPermissions, `org_unit_id=${newOrgUnitId}`)

        return {
          success: true,
          permissions: newPermissions,
          count: newPermissions.length
        }
      } catch (error) {
        console.error('[Auth] Error refreshing permissions:', error)
        throw error
      }
    }
  }
})

